import { EventCustomOverlayDto, EventTypes } from 'defs/events';
import { EventLevelsValues } from 'defs/events';
import guid from 'helper/guid';
import { ProgramType } from 'store/programs';
import { EventsActionType } from './events.actionCreators';
import {
	DISMISSED_ASYNC_EVENT_FAILURE,
	DISMISSED_ASYNC_EVENT_SUCCESS,
	RECEIVED_MY_SUCCESS_ITEMS,
	RECEIVED_PROGRAM,
	REQUEST_ADD_EVENTS,
	REQUEST_ASYNC_EVENTS_FAILURE,
	REQUEST_ASYNC_EVENTS_SUCCESS,
	REQUEST_CUSTOM_OVERLAYS_FAILURE,
	REQUEST_CUSTOM_OVERLAYS_SUCCESS,
	REQUEST_FLUSH_EVENT_BY_IDS,
	REQUEST_FLUSH_EVENTS,
	REQUEST_FLUSH_EVENTS_BY_TYPE_ID,
} from './events.actionTypes';

export type EventType = {
	eventLevel: EventLevelsValues;
	id?: string | number;
	code: string;
	eventType: string;
	payload?: Object;
	event: any;
};

export const CustomOverlayTargets = {
	NoTarget: 'NO_TARGET',
	Dashboard: 'DASHBOARD',
	ProgramFeed: 'PROGRAM_FEED',
	Profile: 'PROFILE',
	Favorites: 'FAVORITES',
	MySuccess: 'MY_SUCCESS',
	ProgramsRecommended: 'PROGRAMS_RECOMMENDED',
	ProgramsFurther: 'PROGRAMS_FURTHER',
	ProgramPreview: 'PROGRAM_PREVIEW',
	External: 'EXTERNAL',
} as const;

export const CustomOverlayTrigger = {
	'program-preview': 'PROGRAM_PREVIEW',
	dashboard: 'DASHBOARD',
	'program-feed': 'PROGRAM_FEED',
	profile: 'PROFILE',
	favorites: 'FAVORITES',
	'my-success': 'MY_SUCCESS',
	'programs-recommended': 'PROGRAMS_RECOMMENDED',
	'programs-further': 'PROGRAMS_FURTHER',
} as const;

type ValueOf<T> = T[keyof T];

export type CustomOverlayType = {
	dateFrom: number;
	dateTo: number;
	external: string | null;
	id: string;
	targetLabel: string;
	closingLabel: string | null;
	onlyExistingUser: boolean;
	programPreview: string | null;
	target: ValueOf<typeof CustomOverlayTargets>;
	text: string;
	title: string;
	trigger: 'ANY' | ValueOf<typeof CustomOverlayTrigger>;
};

export type NormalizedEventType<T extends GenericEventType = EventType> = T & {
	reactId: string;
};

export type EventsType = EventType[];
export type NormalizedEventsType<T extends GenericEventType = EventType> = NormalizedEventType<T>[];

export type EventsStateType = {
	readonly events: NormalizedEventsType;
	readonly customOverlays: NormalizedEventsType<CustomOverlayType>;
	readonly error?: any;
	readonly notifications?: any;
	readonly program: null | ProgramType;
	points: number;
	level: string;
};

export type RootStateType = {
	events: EventsStateType;
};

export type GenericEventType = EventType | CustomOverlayType;

const filterForExisting = (state: EventsStateType, events: EventsType) => {
	const existingIds = state.events.map((event) => event.id);

	if (!Array.isArray(events)) {
		// tslint:disable-next-line:no-console
		console.error('events should be an array but is: ', events);
		events = [events];
	}

	return events.filter((event): boolean => {
		if (~existingIds.indexOf(event.code)) {
			return false;
		}

		const eventTypes = Object.keys(EventTypes).map((eventType) => EventTypes[eventType]);
		if (!eventTypes.includes(event.eventType)) {
			return false;
		}
		return true;
	});
};

const addReactId = <T extends GenericEventType>(event: T): NormalizedEventType<T> => {
	return {
		...event,
		reactId: guid(),
	};
};

export const initialState: EventsStateType = {
	events: [],
	customOverlays: [],
	program: null,
	points: null as any,
	level: null as any,
};

export default (state: EventsStateType = initialState, action: EventsActionType): EventsStateType => {
	switch (action.type) {
		case REQUEST_FLUSH_EVENTS:
			return { ...state, events: [] };
		case REQUEST_FLUSH_EVENTS_BY_TYPE_ID:
			const _action = action;
			const events = ([] as any[])
				.concat(state.events)
				.map((event): NormalizedEventType | null => {
					if (event.eventType !== _action.eventType && _action.eventType !== EventTypes.Point) {
						if (event.reactId !== _action.reactId) {
							return event;
						}
					} else if (action.eventType === EventTypes.Point && _action.reactId === '*') {
						return null;
					}

					return null;
				})
				.filter((event: NormalizedEventType | null): boolean => Boolean(event));

			return {
				...state,
				events: events as NormalizedEventsType,
			};
		// Flush events AND custom overlays
		case REQUEST_FLUSH_EVENT_BY_IDS:
			return {
				...state,
				events: state.events.filter((event): boolean => !action.reactIds.includes(event.reactId)),
				customOverlays: state.customOverlays.filter(
					(customOverlay): boolean => !action.reactIds.includes(customOverlay.reactId)
				),
			};

		case REQUEST_ADD_EVENTS:
			if (!action.events) {
				return state;
			}

			const filteredEvents = filterForExisting(state, action.events);
			const finalEvents = filteredEvents.map(addReactId);

			return {
				...state,
				error: state.error,
				events: [...state.events, ...finalEvents],
			};
		case REQUEST_ASYNC_EVENTS_SUCCESS:
			if (!action.events || (Array.isArray(action.events) && action.events.length === 0)) {
				return state;
			}

			const rawEvents = action.events.map((wrap) => wrap.event);
			const newEvents = filterForExisting(state, rawEvents);

			return {
				...state,
				error: state.error,
				events: [...state.events, ...newEvents.map(addReactId)],
			};
		case REQUEST_ASYNC_EVENTS_FAILURE:
			return {
				...state,
				error: action.error,
			};
		case DISMISSED_ASYNC_EVENT_SUCCESS:
			const _action3 = action;
			const notifications = state.events.map((notification: any): any => {
				if (notification.id !== _action3.id) {
					return notification;
				}

				return null;
			});

			return {
				...state,
				error: state.error,
				notifications,
			};
		case DISMISSED_ASYNC_EVENT_FAILURE:
			return {
				...state,
				error: action.error,
			};
		case REQUEST_CUSTOM_OVERLAYS_SUCCESS:
			if (
				!action.customOverlays ||
				(Array.isArray(action.customOverlays) && action.customOverlays.length === 0)
			) {
				return state;
			}

			const customOverlays = action.customOverlays.map(addReactId);

			return {
				...state,
				error: state.error,
				customOverlays: customOverlays as NormalizedEventsType<CustomOverlayType>,
			};
		case REQUEST_CUSTOM_OVERLAYS_FAILURE:
			return {
				...state,
				error: action.error,
			};
		case RECEIVED_PROGRAM:
			return { ...state, program: action.payload as any };
		case RECEIVED_MY_SUCCESS_ITEMS:
			return {
				...state,
				points: action.contents.points,
				level: action.contents.level,
			};
		default:
			return state;
	}
};

export type PremappedEventsStore = {
	points: Object[];
	stickers: Object[];
	badges: Object[];
	feedbacks: Object[];
	images: Object[];
	warnings: Object[];
	levelUps: Object[];
	programSummaries: Object[];
	messages: Object[];
	customOverlays: EventCustomOverlayDto[];
};
